import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { MenuItem } from '../interfaces/menu-item.interface';
import { trackById } from '@vex/utils/track-by';
import { VexPopoverRef } from '@vex/components/vex-popover/vex-popover-ref';
import { RouterLink } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AdminService } from 'src/app/services/admin.service';

export interface OnlineStatus {
  id: 'online' | 'away' | 'dnd' | 'offline';
  label: string;
  icon: string;
  colorClass: string;
}

@Component({
    selector: 'vex-toolbar-user-dropdown',
    templateUrl: './toolbar-user-dropdown.component.html',
    styleUrls: ['./toolbar-user-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
        NgFor,
        MatRippleModule,
        RouterLink,
        NgClass,
        NgIf
    ]
})
export class ToolbarUserDropdownComponent implements OnInit {
  displayName = this.getDisplayName();
  profileImage = 'assets/images/useravatar.png';

  items: MenuItem[] = [
    {
      id: '1',
      icon: 'mat:account_circle',
      label: 'My Profile',
      description: 'Personal Information',
      colorClass: 'text-teal-600',
      route: '/apps/social'
    },
    {
      id: '2',
      icon: 'mat:move_to_inbox',
      label: 'My Inbox',
      description: 'Messages & Latest News',
      colorClass: 'text-primary-600',
      route: '/apps/chat'
    },
    {
      id: '3',
      icon: 'mat:list_alt',
      label: 'My Projects',
      description: 'Tasks & Active Projects',
      colorClass: 'text-amber-600',
      route: '/apps/scrumboard'
    },
    {
      id: '4',
      icon: 'mat:table_chart',
      label: 'Billing Information',
      description: 'Pricing & Current Plan',
      colorClass: 'text-purple-600',
      route: '/pages/pricing'
    }
  ];

  statuses: OnlineStatus[] = [
    {
      id: 'online',
      label: 'Online',
      icon: 'mat:check_circle',
      colorClass: 'text-green-600'
    },
    {
      id: 'away',
      label: 'Away',
      icon: 'mat:access_time',
      colorClass: 'text-orange-600'
    },
    {
      id: 'dnd',
      label: 'Do not disturb',
      icon: 'mat:do_not_disturb',
      colorClass: 'text-red-600'
    },
    {
      id: 'offline',
      label: 'Offline',
      icon: 'mat:offline_bolt',
      colorClass: 'text-gray-600'
    }
  ];

  activeStatus: OnlineStatus = this.statuses[0];

  trackById = trackById;

  constructor(
    private cd: ChangeDetectorRef,
    private popoverRef: VexPopoverRef<ToolbarUserDropdownComponent>,
    private admin: AdminService
  ) {}

  ngOnInit() {
    this.admin.getUserProfile().subscribe({
      next: (profile: any) => {
        const name = String(profile?.fullname || profile?.username || '').trim();
        const image = String(profile?.profileimage || '').trim();

        if (name) {
          this.displayName = name;
        }

        this.profileImage = image || 'assets/images/useravatar.png';
        this.cd.markForCheck();
      }
    });
  }

  setStatus(status: OnlineStatus) {
    this.activeStatus = status;
    this.cd.markForCheck();
  }

  close() {
    this.popoverRef.close();
  }

  private getDisplayName(): string {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return 'User';

    try {
      const payload: any = JSON.parse(atob(token.split('.')[1]));
      const value =
        payload?.fullname ||
        payload?.fullName ||
        payload?.FullName ||
        payload?.name ||
        payload?.Name ||
        payload?.given_name ||
        payload?.email ||
        payload?.username ||
        payload?.UserName ||
        payload?.unique_name;

      const name = String(value || '').trim();
      return name && !/^\d+$/.test(name) ? name : 'User';
    } catch {
      return 'User';
    }
  }
}
